import { inject, Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthenticationService } from '../../features/auth/services/authentication.service';

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthenticationService);
  private readonly skipRefreshUrls = ['/auth/login', '/auth/refresh-token', '/auth/reset-password'];

  private refreshInFlight$: Observable<void> | null = null;
  private readonly refreshUrl = `${environment.apiUrl}/v1/auth/refresh-token`;

  private refreshToken(): Observable<void> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.http.post(this.refreshUrl, {}, { withCredentials: true }).pipe(
        map(() => void 0),
        shareReplay(1),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
      );
    }

    return this.refreshInFlight$;
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: unknown) => {
        if (!(error instanceof HttpErrorResponse)) {
          return throwError(() => error);
        }

        const isUnauthorized = error.status === 401;
        const shouldSkip = this.skipRefreshUrls.some((url) => req.url.includes(url));

        if (!isUnauthorized || shouldSkip) {
          return throwError(() => error);
        }

        return this.refreshToken().pipe(
          switchMap(() => next.handle(req)),
          catchError((refreshError) => {
            this.authService.clearAuthState();
            return throwError(() => refreshError);
          }),
        );
      }),
    );
  }
}
